// Печать этикеток для FBS-отправлений на стадии «Ожидают отгрузки».
// Переиспользует уже существующий клиентский рендер ЧЗ Data Matrix
// (см. free-tools/chestnyZnakPrint.js) и JsBarcode для обычных штрихкодов —
// это ровно то же самое, чем пользуется страница «Бесплатные инструменты»,
// просто вызвано программно из карточки заказа вместо формы.
import JsBarcode from 'jsbarcode';
import { dataMatrixDataUrl } from '../../../free-tools/chestnyZnakPrint';

function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    }[c]));
}

function openPrintWindow(html, { onReady } = {}) {
    const win = window.open('', '_blank', 'width=520,height=420');
    if (!win) return null;
    win.document.write(html);
    win.document.close();
    win.onload = () => {
        Promise.resolve(onReady ? onReady(win) : null).then(() => {
            setTimeout(() => { try { win.print(); } catch { /* ignore */ } }, 250);
        });
    };
    return win;
}

const LABEL_PAGE_STYLE = `
    @page { size: 58mm 40mm; margin: 0; }
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, sans-serif; }
    .pb { page-break-after: always; }
    .label {
        width: 58mm; height: 40mm; padding: 1.5mm;
        display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 1.5mm;
        overflow: hidden;
    }
    .label svg, .label img { max-width: 100%; max-height: 26mm; display: block; }
    .label .lbl-title {
        font-size: 8pt; font-weight: 600; text-align: center; line-height: 1.15;
        max-width: 100%; overflow-wrap: anywhere;
    }
`;

/**
 * Печать одной или нескольких этикеток «штрихкод + название товара».
 * items: [{ barcode, name }]
 */
export function printProductBarcodeLabels(items) {
    const printable = (items || []).filter((it) => it && it.barcode);
    if (printable.length === 0) return null;

    const pages = printable.map((_, i) => `
        <div class="label">
            <svg id="pbl-${i}"></svg>
            <div class="lbl-title">${escapeHtml(printable[i].name || '')}</div>
        </div>`).join("<div class='pb'></div>");

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/><style>${LABEL_PAGE_STYLE}</style></head><body>${pages}</body></html>`;

    return openPrintWindow(html, {
        onReady: (win) => {
            printable.forEach((it, i) => {
                const el = win.document.getElementById(`pbl-${i}`);
                if (!el) return;
                try {
                    JsBarcode(el, it.barcode, {
                        format: 'CODE128', lineColor: '#000', background: '#fff',
                        width: 1.6, height: 46, displayValue: true, fontSize: 12, margin: 4,
                    });
                } catch { /* ignore malformed barcode */ }
            });
        },
    });
}

/**
 * Печать этикеток Честного Знака (Data Matrix) по списку кодов маркировки.
 * marks: string[]
 */
export function printChestnyZnakLabels(marks) {
    const printable = (marks || []).filter(Boolean);
    if (printable.length === 0) return null;

    const pages = printable.map((_, i) => `
        <div class="label">
            <img id="cz-${i}" alt="ЧЗ"/>
            <div class="lbl-title">Честный знак</div>
        </div>`).join("<div class='pb'></div>");

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/><style>${LABEL_PAGE_STYLE}</style></head><body>${pages}</body></html>`;

    return openPrintWindow(html, {
        onReady: (win) => {
            printable.forEach((mark, i) => {
                const el = win.document.getElementById(`cz-${i}`);
                if (!el) return;
                try {
                    el.src = dataMatrixDataUrl(mark, 300);
                } catch { /* ignore malformed mark */ }
            });
        },
    });
}

/**
 * Открыть в новой вкладке печатную форму Ozon (QR-этикетку заказа) — сама Ozon
 * присылает готовый PDF, конвертировать/дорисовывать его на клиенте не нужно.
 */
export function openBase64Pdf(base64, fileName) {
    if (!base64) return null;
    try {
        const byteChars = atob(base64);
        const byteNumbers = new Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) byteNumbers[i] = byteChars.charCodeAt(i);
        const blob = new Blob([new Uint8Array(byteNumbers)], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        const win = window.open(url, '_blank');
        if (!win) {
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName || 'label.pdf';
            a.click();
        }
        setTimeout(() => URL.revokeObjectURL(url), 60000);
        return win;
    } catch {
        return null;
    }
}

/**
 * Разбивает отправления на две группы для последовательной печати:
 * сначала те, что содержат только один артикул (можно сгруппировать по
 * артикулу и печатать подряд), затем сборные (несколько разных товаров).
 */
export function groupPostingsForLabelPrint(postings) {
    const single = [];
    const mixed = [];
    (postings || []).forEach((p) => {
        const items = Array.isArray(p.items) ? p.items : [];
        const skus = new Set(items.map((i) => i.offer_id || i.sku).filter(Boolean));
        if (skus.size <= 1) single.push(p);
        else mixed.push(p);
    });
    single.sort((a, b) => {
        const skuA = (a.items && a.items[0] && (a.items[0].offer_id || a.items[0].sku)) || '';
        const skuB = (b.items && b.items[0] && (b.items[0].offer_id || b.items[0].sku)) || '';
        return String(skuA).localeCompare(String(skuB));
    });
    return [...single, ...mixed];
}
